// src/pages/Candidates.js
import { Fragment, useContext, useEffect, useMemo, useState } from "react";
import Navbar from "../components/Navbar";
import { AuthContext } from "../contexts/AuthContext";
import {
  apiFetch,
  fetchOrganizations,
  getSelectedOrgId,
  setSelectedOrgId,
} from "../services/api";
import { candidateStatusLabel } from "../utils/badges";
import { useNavigate } from "react-router-dom";

const PAGE_SIZES = [10, 20, 50, 100];

export default function Candidates() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const isSuper =
    String(user?.group_role || user?.role || user?.user_group_name || "")
      .toLowerCase() === "superadmin";

  const [orgs, setOrgs] = useState([]);
  const [orgFilter, setOrgFilter] = useState(getSelectedOrgId() ?? null);

  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [search, setSearch] = useState("");

  const [expanded, setExpanded] = useState({});
  const [appsByCandidate, setAppsByCandidate] = useState({});
  const [appsLoading, setAppsLoading] = useState({});

  const [form, setForm] = useState({
    candidate_id: null,
    candidate_name: "",
    candidate_email: "",
    candidate_phone: "",
    candidate_status: 1,
    organization_id: isSuper ? orgFilter ?? "" : user?.organization_id ?? "",
    candidate_resume: null, // optional candidate-level resume (legacy; submissions carry resumes now)
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const orgOptions = useMemo(() => {
    const list = [{ value: "", label: "All organizations" }];
    for (const o of orgs || []) {
      if (!o) continue;
      const id = Number(o.organization_id ?? o.id ?? o.value);
      const name = String(o.organization_name ?? o.name ?? o.label ?? "");
      if (Number.isFinite(id)) list.push({ value: id, label: name });
    }
    return list;
  }, [orgs]);

  const jobDecisionLabel = (d) => {
    const n = Number(d);
    if (n === 0) return "Not Considered";
    if (n === 1) return "In Progress";
    if (n === 2) return "Hired";
    if (n === 3) return "AI Recommended";
    return "-";
  };

  // Latest submission resume (proxy on dashboard backend)
  const resumeProxyUrl = (candidateId) =>
    (candidateId ? `/api/candidates/${candidateId}/resume` : null);

  const initials = (name) => {
    if (!name) return "?";
    const parts = String(name).trim().split(/\s+/);
    const a = (parts[0]?.[0] || "").toUpperCase();
    const b = (parts[1]?.[0] || "").toUpperCase();
    return (a + b).trim() || a || "?";
  };

  const orgQueryForFetch = () => {
    if (!isSuper) return "";
    if (orgFilter == null || orgFilter === "") return "?all=1";
    return `?organization_id=${encodeURIComponent(orgFilter)}`;
  };

  async function fetchCandidatesList() {
    setLoading(true);
    try {
      let url = `/api/candidates${orgQueryForFetch()}`;
      let data = await apiFetch(url).catch(() => null);

      if (!Array.isArray(data)) {
        if (isSuper && orgFilter != null && orgFilter !== "") {
          data = await apiFetch(`/api/candidates?orgId=${orgFilter}`).catch(
            () => null
          );
        }
      }
      if (!Array.isArray(data)) {
        data = await apiFetch(`/api/candidates`).catch(() => []);
      }

      setCandidates(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  }

  async function fetchOrganizationsIfNeeded() {
    if (!isSuper) return;
    try {
      const list = await fetchOrganizations();
      setOrgs(Array.isArray(list) ? list : []);
    } catch {
      setOrgs([]);
    }
  }

  useEffect(() => { fetchOrganizationsIfNeeded(); }, []); // eslint-disable-line
  useEffect(() => { fetchCandidatesList(); }, [isSuper, orgFilter]); // eslint-disable-line

  useEffect(() => {
    if (isSuper) {
      setForm((f) => ({
        ...f,
        organization_id:
          orgFilter ?? f.organization_id ?? user?.organization_id ?? "",
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgFilter, isSuper]);

  async function fetchCandidateApplications(candidateId) {
    setAppsLoading((m) => ({ ...m, [candidateId]: true }));
    try {
      const suffixes = ["applications", "submissions", "jobs"];
      const q = orgQueryForFetch();
      let rows = null;

      for (const sfx of suffixes) {
        const url = `/api/candidates/${candidateId}/${sfx}${q}`;
        // eslint-disable-next-line no-await-in-loop
        const data = await apiFetch(url).catch(() => null);
        if (Array.isArray(data)) { rows = data; break; }
      }

      if (!Array.isArray(rows)) rows = [];

      const normalized = rows.map((r) => ({
        job_req_id: r.job_req_id ?? r.id ?? r.jobId ?? r.job_reqid,
        job_req_title: r.job_req_title ?? r.title ?? r.job_title,
        job_area_of_interest_descr: r.job_area_of_interest_descr ?? r.area_of_interest ?? r.aoi,
        job_location_descr: r.job_location_descr ?? r.location_descr ?? r.location,
        country_name: r.country_name ?? r.country,
        job_decision: r.job_decision ?? r.status ?? r.decision ?? r.job_req_status ?? 0,
        organization_id: r.organization_id ?? orgFilter ?? user?.organization_id ?? null,
      }));
      setAppsByCandidate((m) => ({ ...m, [candidateId]: normalized }));
    } finally {
      setAppsLoading((m) => ({ ...m, [candidateId]: false }));
    }
  }

  // ——— NEW: Prefer job-scoped proxy (works on Jobs page) when opening resume ———
  async function getLatestJobId(candidateId) {
    // Use cached applications if present
    let apps = appsByCandidate[candidateId];
    if (!apps) {
      await fetchCandidateApplications(candidateId);
      apps = appsByCandidate[candidateId];
    }
    const jobId = Array.isArray(apps) && apps.length > 0 ? Number(apps[0]?.job_req_id) : null;
    return Number.isFinite(jobId) ? jobId : null;
  }

  async function handleDownloadResume(e, candidateId) {
    // Open via job-scoped proxy if possible, else fallback to candidate endpoint
    try {
      e?.preventDefault?.();
      e?.stopPropagation?.();

      const jobId = await getLatestJobId(candidateId);
      const url = jobId
        ? `/api/jobs/${jobId}/candidates/${candidateId}/resume`
        : `/api/candidates/${candidateId}/resume`;

      // Use a new tab to keep UX consistent with a normal download link
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      // Fallback to candidate endpoint if anything goes wrong
      const fallback = `/api/candidates/${candidateId}/resume`;
      window.open(fallback, "_blank", "noopener,noreferrer");
    }
  }

  const toggleExpand = (candidate) => {
    const id = Number(candidate?.candidate_id ?? candidate?.id);
    if (!Number.isFinite(id)) return;
    setExpanded((m) => {
      const next = { ...m, [id]: !m[id] };
      if (!m[id] && !appsByCandidate[id]) {
        fetchCandidateApplications(id);
      }
      return next;
    });
  };

  const scheduleInterview = (orgId, jobId, candidateId) => {
    const query = new URLSearchParams();
    query.set("create", "1");
    query.set("autoSelect", "1");
    if (jobId != null) query.set("jobId", String(jobId));
    if (candidateId != null) {
      query.set("candidateId", String(candidateId));
      query.set("candidate_id", String(candidateId));
    }
    if (orgId != null) query.set("orgId", String(orgId));
    navigate(`/interviews?${query.toString()}`);
  };

  function resetForm() {
    setForm({
      candidate_id: null,
      candidate_name: "",
      candidate_email: "",
      candidate_phone: "",
      candidate_status: 1,
      organization_id: isSuper ? orgFilter ?? "" : user?.organization_id ?? "",
      candidate_resume: null,
    });
    setMessage(null);
  }

  function loadCandidateToForm(c) {
    setForm({
      candidate_id: Number(c?.candidate_id ?? c?.id) || null,
      candidate_name: c?.candidate_name ?? c?.name ?? "",
      candidate_email: c?.candidate_email ?? "",
      candidate_phone: c?.candidate_phone ?? "",
      candidate_status: Number(c?.candidate_status ?? c?.status ?? 1),
      organization_id:
        (isSuper
          ? c?.organization_id ?? orgFilter ?? user?.organization_id ?? ""
          : user?.organization_id ?? "") || "",
      candidate_resume: null,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function onFormFileChange(e) {
    const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
    setForm((f) => ({ ...f, candidate_resume: file }));
  }

  async function sendFormData(url, method, fd) {
    const token =
      user?.token ||
      localStorage.getItem("token") ||
      localStorage.getItem("authToken") ||
      sessionStorage.getItem("token") ||
      "";
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

    const res = await fetch(url, { method, body: fd, headers, credentials: "include" });
    const text = await res.text().catch(() => "");
    if (!res.ok) throw new Error(text || `HTTP ${res.status}`);
    try { return JSON.parse(text || "{}"); } catch { return {}; }
  }

  async function saveCandidate(e) {
    e?.preventDefault?.();
    setSaving(true);
    setMessage(null);
    try {
      const idNum = Number(form.candidate_id);
      const isEdit = Number.isInteger(idNum) && idNum > 0;

      const url = isEdit ? `/api/candidates/${idNum}` : `/api/candidates`;
      const method = isEdit ? "PUT" : "POST";

      const fd = new FormData();
      fd.append("candidate_name", String(form.candidate_name || "").trim());
      fd.append("candidate_email", String(form.candidate_email || "").trim());
      if (form.candidate_phone != null) fd.append("candidate_phone", String(form.candidate_phone));
      if (form.candidate_status != null) fd.append("candidate_status", String(form.candidate_status));

      const effOrgId = isSuper
        ? form.organization_id ?? orgFilter ?? user?.organization_id ?? ""
        : user?.organization_id ?? "";
      if (effOrgId !== "") fd.append("organization_id", String(effOrgId));

      if (form.candidate_resume instanceof File) {
        fd.append("candidate_resume", form.candidate_resume);
      }

      if (!fd.get("candidate_name")) {
        setSaving(false); setMessage({ type: "error", text: "Name is required." }); return;
      }
      if (!fd.get("candidate_email")) {
        setSaving(false); setMessage({ type: "error", text: "Email is required." }); return;
      }

      await sendFormData(url, method, fd);
      setMessage({ type: "success", text: isEdit ? "Candidate updated." : "Candidate created." });
      await fetchCandidatesList();
      resetForm();
    } catch (err) {
      console.error("saveCandidate error:", err);
      const msg =
        /\b401\b/.test(String(err)) || /Unauthorized/i.test(String(err))
          ? "Unauthorized. Please sign in again."
          : err?.message?.replace(/^Error:\s*/i, "") || "Failed to save candidate.";
      setMessage({ type: "error", text: msg });
    } finally {
      setSaving(false);
    }
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return candidates;
    return (candidates || []).filter((c) => {
      const name = String(c?.candidate_name ?? "").toLowerCase();
      const email = String(c?.candidate_email ?? "").toLowerCase();
      const phone = String(c?.candidate_phone ?? "").toLowerCase();
      return name.includes(q) || email.includes(q) || phone.includes(q);
    });
  }, [candidates, search]);

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const paginated = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, safePage, pageSize]);

  useEffect(() => { setPage(1); }, [orgFilter, pageSize, total, search]);

  const onChangeOrg = (e) => {
    const v = e.target.value;
    const next = v === "" ? null : Number(v);
    setOrgFilter(next);
    setSelectedOrgId(next);
  };

  function asCsvCell(v) {
    const s = String(v ?? "");
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  }

  function exportCsv(rows, filename = "candidates.csv") {
    const header = ["Name", "Email", "Phone", "Status"];
    const lines = [header.map(asCsvCell).join(",")];
    for (const c of rows) {
      const status = candidateStatusLabel
        ? candidateStatusLabel(c?.candidate_status ?? c?.status)
        : String(c?.candidate_status ?? c?.status ?? "");
      lines.push(
        [c?.candidate_name ?? "", c?.candidate_email ?? "", c?.candidate_phone ?? "", status]
          .map(asCsvCell).join(",")
      );
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  }

  function exportPdfLike(rows) {
    const html = `
<!doctype html>
<html>
<head>
<meta charset="utf-8"/>
<title>Candidates</title>
<style>
  body{ font-family: system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif; padding:16px; }
  h1{ font-size:16px; margin:0 0 12px; }
  table{ width:100%; border-collapse: collapse; }
  th, td{ border:1px solid #ddd; padding:8px; font-size:12px; }
  th{ background:#f5f5f5; text-align:left; }
</style>
</head>
<body>
  <h1>Candidates</h1>
  <table>
    <thead>
      <tr><th>Name</th><th>Email</th><th>Phone</th><th>Status</th></tr>
    </thead>
    <tbody>
      ${rows.map((c) => {
        const status = candidateStatusLabel
          ? candidateStatusLabel(c?.candidate_status ?? c?.status)
          : String(c?.candidate_status ?? c?.status ?? "");
        return `<tr>
          <td>${(c?.candidate_name ?? "").toString().replace(/</g, "&lt;")}</td>
          <td>${(c?.candidate_email ?? "").toString().replace(/</g, "&lt;")}</td>
          <td>${(c?.candidate_phone ?? "").toString().replace(/</g, "&lt;")}</td>
          <td>${status.toString().replace(/</g, "&lt;")}</td>
        </tr>`;
      }).join("")}
    </tbody>
  </table>
  <script>window.onload = () => { window.print(); setTimeout(() => window.close(), 500); };</script>
</body>
</html>`;
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.open(); w.document.write(html); w.document.close();
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-xl font-semibold text-gray-900">Candidates</h1>
          {isSuper && (
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-600">Organization</label>
              <select className="border rounded-md px-2 py-1 text-sm bg-white" value={orgFilter ?? ""} onChange={onChangeOrg}>
                {orgOptions.map((o) => (<option key={`${o.value}-${o.label}`} value={o.value}>{o.label}</option>))}
              </select>
            </div>
          )}
        </div>

        {/* Create / Edit */}
        <form onSubmit={saveCandidate} className="bg-white border rounded-lg shadow-sm overflow-hidden mb-6">
          <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between">
            <div className="text-sm font-medium text-gray-800">
              {form.candidate_id ? "Edit Candidate" : "Add New Candidate"}
            </div>
            {form.candidate_id && (
              <button type="button" className="text-sm text-gray-600 hover:text-gray-900 underline" onClick={resetForm}>
                Cancel edit
              </button>
            )}
          </div>

          <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {isSuper && (
              <div>
                <label className="block text-sm text-gray-700 mb-1">Organization</label>
                <select
                  className="w-full border rounded-md px-3 py-2 bg-white"
                  value={form.organization_id ?? ""}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      organization_id: e.target.value === "" ? "" : Number(e.target.value),
                    }))
                  }
                >
                  <option value="">Select organization</option>
                  {orgs.map((o) => (
                    <option key={`org-${o.organization_id}`} value={o.organization_id}>
                      {o.organization_name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className="block text-sm text-gray-700 mb-1">Name</label>
              <input
                type="text"
                className="w-full border rounded-md px-3 py-2"
                value={form.candidate_name}
                onChange={(e) => setForm((f) => ({ ...f, candidate_name: e.target.value }))}
                placeholder="Full name"
                required
              />
            </div>

            <div>
              <label className="block text-sm text-gray-700 mb-1">Email</label>
              <input
                type="email"
                className="w-full border rounded-md px-3 py-2"
                value={form.candidate_email}
                onChange={(e) => setForm((f) => ({ ...f, candidate_email: e.target.value }))}
                placeholder="email@example.com"
                required
              />
            </div>

            <div>
              <label className="block text-sm text-gray-700 mb-1">Phone</label>
              <input
                type="text"
                className="w-full border rounded-md px-3 py-2"
                value={form.candidate_phone}
                onChange={(e) => setForm((f) => ({ ...f, candidate_phone: e.target.value }))}
                placeholder="+20 1X XXX XXXX"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-700 mb-1">Status</label>
              <select
                className="w-full border rounded-md px-3 py-2 bg-white"
                value={form.candidate_status}
                onChange={(e) => setForm((f) => ({ ...f, candidate_status: Number(e.target.value) }))}
              >
                <option value={1}>Active</option>
                <option value={0}>Inactive</option>
              </select>
            </div>

            <div>
              <label className="block text-sm text-gray-700 mb-1">
                Resume (optional)
              </label>
              <input
                type="file"
                accept=".pdf,.doc,.docx,.txt"
                className="w-full border rounded-md px-3 py-2 bg-white"
                onChange={onFormFileChange}
              />
              {/* We keep this input for legacy candidate-level uploads.
                  Submission resumes live on JOB_REQ_SUBMISSION and are shown per job below. */}
            </div>
          </div>

          {message && (
            <div className={`px-4 py-2 ${
              message.type === "success" ? "text-green-700 bg-green-50 border-t border-green-200" : "text-red-700 bg-red-50 border-t border-red-200"
            }`}>
              {message.text}
            </div>
          )}

          <div className="px-4 py-3 border-t bg-gray-50 flex items-center justify-end space-x-3">
            <button type="button" onClick={resetForm} className="px-3 py-2 border rounded-md text-sm bg-white hover:bg-gray-50" disabled={saving}>
              Reset
            </button>
            <button type="submit" className="px-4 py-2 rounded-md text-sm bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50" disabled={saving}>
              {saving ? "Saving…" : form.candidate_id ? "Update Candidate" : "Create Candidate"}
            </button>
          </div>
        </form>

        {/* List */}
        <div className="bg-white border rounded-lg shadow-sm overflow-hidden">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 px-4 py-3 border-b bg-gray-50">
            <div className="flex-1 flex items-center gap-2">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, email, or phone…"
                className="w-full md:w-80 border rounded-md px-3 py-2 text-sm bg-white"
              />
              {search && (
                <button className="px-3 py-2 border rounded-md text-sm bg-white hover:bg-gray-50" onClick={() => setSearch("")}>
                  Clear
                </button>
              )}
            </div>

            <div className="flex items-center gap-2">
              <button className="px-3 py-2 border rounded-md text-sm bg-white hover:bg-gray-50" onClick={() => exportCsv(filtered, "candidates.csv")}>
                Export CSV
              </button>
              <button className="px-3 py-2 border rounded-md text-sm bg-white hover:bg-gray-50" onClick={() => exportPdfLike(filtered)}>
                Export PDF
              </button>
            </div>

            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">Rows per page</span>
                <select
                  className="border rounded-md px-2 py-1 text-sm bg-white"
                  value={pageSize}
                  onChange={(e) => {
                    const n = Number(e.target.value);
                    if (Number.isFinite(n)) setPageSize(n);
                    setPage(1);
                  }}
                >
                  {PAGE_SIZES.map((n) => (<option key={n} value={n}>{n}</option>))}
                </select>
              </div>

              <div className="flex items-center space-x-1">
                <button className="px-2 py-1 border rounded-md text-sm bg-white hover:bg-gray-50 disabled:opacity-50" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={safePage <= 1}>
                  Prev
                </button>
                <span className="text-sm text-gray-700 px-1">{safePage} / {totalPages}</span>
                <button className="px-2 py-1 border rounded-md text-sm bg-white hover:bg-gray-50 disabled:opacity-50" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages}>
                  Next
                </button>
              </div>
            </div>
          </div>

          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Phone</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Resume (latest)</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>

            <tbody className="bg-white divide-y divide-gray-100">
              {!loading &&
                paginated.map((c) => {
                  const id = Number(c?.candidate_id ?? c?.id);
                  const resumeUrl = resumeProxyUrl(id); // candidate endpoint (fallback)
                  const isOpen = !!expanded[id];

                  return (
                    <Fragment key={`cand-${id}`}>
                      <tr className={`cursor-pointer transition-colors hover:bg-indigo-50`} onClick={() => toggleExpand(c)} aria-expanded={isOpen}>
                        <td className="px-4 py-3 text-sm">
                          <div className="flex items-center space-x-3">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"
                              className={`w-4 h-4 text-gray-500 transform transition-transform ${isOpen ? "rotate-90" : ""}`}>
                              <path fillRule="evenodd" d="M6.293 7.293a1 1 0 011.414 0L12 11.586l-4.293 4.293a1 1 0 01-1.414-1.414L9.586 11.586 6.293 8.707a1 1 0 010-1.414z" clipRule="evenodd"/>
                            </svg>
                            <div className="flex items-center space-x-2">
                              <div className="h-7 w-7 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-xs font-semibold">
                                {initials(c?.candidate_name)}
                              </div>
                              <span className="font-medium text-gray-900">{c?.candidate_name ?? "—"}</span>
                            </div>
                          </div>
                        </td>

                        <td className="px-4 py-3 text-sm text-gray-700">{c?.candidate_email ?? "—"}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{c?.candidate_phone ?? "—"}</td>
                        <td className="px-4 py-3 text-sm">
                          {candidateStatusLabel
                            ? candidateStatusLabel(c?.candidate_status ?? c?.status)
                            : String(c?.candidate_status ?? c?.status ?? "")}
                        </td>

                        <td className="px-4 py-3 text-sm">
                          {resumeUrl ? (
                            <a
                              href={resumeUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              download
                              className="inline-flex items-center text-blue-600 hover:text-blue-800"
                              onClick={(e) => handleDownloadResume(e, id)}
                              title="Download latest resume"
                            >
                              Download
                            </a>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>

                        <td className="px-4 py-3 text-sm">
                          <div className="inline-flex items-center space-x-2" onClick={(e) => e.stopPropagation()}>
                            <button className="px-2 py-1 border rounded-md text-sm bg-white hover:bg-gray-50" onClick={() => loadCandidateToForm(c)}>
                              Edit
                            </button>
                            <button className="px-2 py-1 border rounded-md text-sm bg-white hover:bg-red-50 text-red-600" onClick={() => apiFetch(`/api/candidates/${id}`, { method: "DELETE" }).then(fetchCandidatesList)}>
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>

                      {isOpen && (
                        <tr className="bg-gray-50">
                          <td colSpan={6} className="px-4 py-4">
                            <div className="border rounded-lg bg-white shadow-sm p-3">
                              <div className="text-sm text-gray-800 mb-2 font-medium">Applications</div>
                              {appsLoading[id] && <div className="text-sm text-gray-500">Loading…</div>}
                              {!appsLoading[id] && (!appsByCandidate[id] || appsByCandidate[id].length === 0) && (
                                <div className="text-sm text-gray-500">No applications</div>
                              )}
                              {!appsLoading[id] && Array.isArray(appsByCandidate[id]) && appsByCandidate[id].length > 0 && (
                                <div className="overflow-x-auto">
                                  <table className="min-w-full divide-y divide-gray-200 border rounded">
                                    <thead className="bg-gray-100">
                                      <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        <th className="px-3 py-2">Job ID</th>
                                        <th className="px-3 py-2">Title</th>
                                        <th className="px-3 py-2">Area of Interest</th>
                                        <th className="px-3 py-2">Location</th>
                                        <th className="px-3 py-2">Status</th>
                                        <th className="px-3 py-2">Action</th>
                                      </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-100">
                                      {appsByCandidate[id].map((j) => {
                                        const jobId = Number(j.job_req_id);
                                        const locParts = [];
                                        if (j.job_location_descr) locParts.push(j.job_location_descr);
                                        if (j.country_name) locParts.push(j.country_name);
                                        const loc = locParts.length ? locParts.join(", ") : "—";
                                        const canSchedule = Number(j.job_decision) === 1;
                                        const orgId = j.organization_id ?? orgFilter ?? user?.organization_id ?? null;

                                        return (
                                          <tr key={`job-${id}-${jobId}`}>
                                            <td className="px-3 py-2 text-sm">{jobId || "—"}</td>
                                            <td className="px-3 py-2 text-sm">{j.job_req_title || "—"}</td>
                                            <td className="px-3 py-2 text-sm">{j.job_area_of_interest_descr || "—"}</td>
                                            <td className="px-3 py-2 text-sm">{loc}</td>
                                            <td className="px-3 py-2 text-sm">{jobDecisionLabel(j.job_decision)}</td>
                                            <td className="px-3 py-2 text-sm">
                                              <div className="flex items-center gap-2">
                                                <button
                                                  className="px-2 py-1 border rounded-md text-sm bg-white hover:bg-gray-50 disabled:opacity-50"
                                                  disabled={!canSchedule}
                                                  onClick={() => scheduleInterview(orgId, jobId, id)}
                                                >
                                                  Schedule Interview
                                                </button>
                                                <a
                                                  href={`/api/jobs/${jobId}/candidates/${id}/resume`}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  className="px-2 py-1 border rounded-md text-sm bg-white hover:bg-gray-50"
                                                  onClick={(e) => e.stopPropagation()}
                                                >
                                                  Download Resume
                                                </a>
                                              </div>
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}

              {!loading && paginated.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-500">No candidates</td></tr>
              )}
              {loading && (
                <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-500">Loading…</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
